//Position model
/**
 * @description Coords represents a geographical point in the form of a latitude and a longitude
 */
export class Coords {

    /**
     * @description Latitude: the angular distance of a place north or south of the earth's equator
     */
    public lat: number = null;

    /**
     * @description Longitude, the angular distance of a place east or west of the Greenwich meridian
     */
    public long: number = null;
    public properties: any = { heading: -1, speed: -1 };
    public isValid: boolean = null;

    constructor(lat: number, long: number, isValid: boolean = true) {
        this.setPosition(lat, long, isValid);
    }

    /**
     * Create a new Coords object from GeoJSON like geometry
     */
    public static fromGeometry(geometry: { type: 'Point', coordinates: number[] }) {
        if(!geometry) {
            console.warn("Geometry point is undefined, cannot convert to coords");
            return new Coords(1, 1, false);
        }
        if(geometry.type != 'Point' || !geometry.coordinates) {
            console.warn("Converting invalid geometry to coords ", geometry);
            return new Coords(1, 1, false);
        }
        return new Coords(geometry.coordinates[1], geometry.coordinates[0]);
    }

    public copy() {
        return new Coords(this.lat, this.long, this.isValid);
    }

    public latLong(): [number, number] {
        return [this.lat, this.long];
    }

    public longLat(): [number, number] {
        return [this.long || 1, this.lat || 1];
    }

    public equals(coords: Coords): boolean {
        return (this.lat == coords.lat && this.long == coords.long && this.isValid == coords.isValid);
    }

    public toJson(): { lat: number, long: number } {
        return { lat: this.lat, long: this.long };
    }

    public static fromJson(json: { lat: number, long: number }): Coords {
        if(!json) return new Coords(55, 12, false);
        else return new Coords(json.lat, json.long, true);
    }

    public asString(seperator: string = ', ', reverse: boolean = false): string {
        let first = this.lat;
        let last = this.long;

        // Reverse option for stupid longlat
        if(reverse) {
            first = this.long;
            last = this.lat;
        }

        return first + seperator + last;
    }

    public setPosition(lat: number, long: number, isValid: boolean = this.isValid) { // isvalid is equal to previous value if not set
        this.lat = lat;
        this.long = long;
        this.isValid = isValid && !isNaN(this.lat) && !isNaN(this.long);

        if(this.lat == 0 && this.long == 0) this.isValid = false; // Set this as false if it is directly on 0,0 - it simply is not
    }

    public setHeading(heading: number, speed: number = -1) {
        this.properties.heading = (heading) ? Math.round(heading) : -1;
        this.properties.speed = (speed) ? Math.round(speed * 10) / 10 : -1;
    }

    public setGeometry(pos: any, isValid: boolean = this.isValid): boolean { // isvalid is equal to previous value if not set
        if(!pos || !pos.coordinates || (pos.coordinates[0] == undefined || pos.coordinates[1] == undefined)) {
            console.error("Failed to set position from geometry array: ", pos);
            this.isValid = false;
            return false;
        }
        if(pos.type == "LineString" && pos.coordinates) {
            this.long = pos.coordinates[0][0];
            this.lat = pos.coordinates[0][1];
        }
        else {
            this.long = parseFloat(pos.coordinates[0]);
            this.lat = parseFloat(pos.coordinates[1]);
        }

        this.isValid = isValid;
        return true;
    }

    public calcDistanceToMe(coords: Coords): number {
        return Coords.calcDistance(this, coords);
    }

    /**
    * Calculate the distance between all coordinates in an array of Coords
    */
    public static calcDistanceInCoordsArray(coords: Coords[]): number {
        let distance: number = 0;
        let lastCoords: Coords;
        for(let c of coords) {
            if(lastCoords) {
                let betweenThisAndLastCoords: number = Coords.calcDistance(c, lastCoords);
                distance = distance + betweenThisAndLastCoords;
            }
            lastCoords = c;
        }
        return distance;
    }

    /**
    * Calculate the distance between two Coords (coords1 and coords2) in METERS
    * From: http://www.geodatasource.com/developers/javascript
    */
    public static calcDistance(coords1: Coords, coords2: Coords): number {
        let lat1 = coords1.lat;
        let lat2 = coords2.lat;
        let lon1 = coords1.long;
        let lon2 = coords2.long;

        if((lat1 == lat2) && (lon1 == lon2)) {
            return 0;
        }
        else {
            var radlat1 = Math.PI * lat1 / 180;
            var radlat2 = Math.PI * lat2 / 180;
            var theta = lon1 - lon2;
            var radtheta = Math.PI * theta / 180;
            var dist = Math.sin(radlat1) * Math.sin(radlat2) + Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta);
            if(dist > 1) {
                dist = 1;
            }
            dist = Math.acos(dist);
            dist = dist * 180 / Math.PI;
            dist = dist * 60 * 1.1515;
            dist = dist * 1.609344 * 1000;
            return dist; // meters 
        }
    }

    /**
    * Calculate the center/average of multiple GeoLocation coordinates (Coords[])
    * From: http://stackoverflow.com/a/14231286/538646
    */
    public static calcAverageCoords(coords: Coords[]): Coords {
        if(coords?.length == 0) {
            console.warn('Cannot calculate the center position: No coordinates provided');
            return null;
        }

        if(coords.length === 1) {
            return coords[0];
        }

        // Temporary values for averages
        let x = 0.0;
        let y = 0.0;
        let z = 0.0;

        for(let coord of coords) {
            let latitude = coord.lat * Math.PI / 180;
            let longitude = coord.long * Math.PI / 180;

            // Do the magic
            x += Math.cos(latitude) * Math.cos(longitude);
            y += Math.cos(latitude) * Math.sin(longitude);
            z += Math.sin(latitude);
        }

        // Average all values
        let total = coords.length;
        x = x / total;
        y = y / total;
        z = z / total;

        // More magic
        let centralLongitude = Math.atan2(y, x);
        let centralSquareRoot = Math.sqrt(x * x + y * y);
        let centralLatitude = Math.atan2(z, centralSquareRoot);

        return new Coords(
            centralLatitude * 180 / Math.PI,
            centralLongitude * 180 / Math.PI,
            true
        );
    }
}
